import React from 'react';
import ChatSidePanelCard from '../sidepanel/ChatSidePanelCard';
import ChatSidePanelSection from '../sidepanel/ChatSidePanelSection';
import ChatSidePanelGroupedPeopleList from './ChatSidePanelGroupedPeopleList';
import ChatSidePanelPeopleList from './ChatSidePanelPeopleList';
import { getArtistFallbackAvatarStyle } from './groupParticipantsPanelConfigUtils';

export default function EventParticipantsSidePanelCard({
  hasConciergeParticipants = false,
  participantCounts = {},
  effectiveGroupEventCount = 0,
  adminParticipants = [],
  conciergeParticipants = [],
  artistParticipantsByPart = [],
  participantPartScopeTabs = [],
  participantPartScopeEventUuid = 'all',
  onChangeParticipantPartScope,
  membersVisible = true,
  hiddenArtistsText = '参加メンバー決定後公開',
  onSelectAdmin,
  onSelectConcierge,
  onSelectArtist,
}) {
  const safeAdminParticipants = Array.isArray(adminParticipants) ? adminParticipants : [];
  const safeConciergeParticipants = Array.isArray(conciergeParticipants) ? conciergeParticipants : [];
  const safeArtistGroups = Array.isArray(artistParticipantsByPart) ? artistParticipantsByPart : [];
  const safeTabs = Array.isArray(participantPartScopeTabs) ? participantPartScopeTabs : [];
  const showScope = Number(effectiveGroupEventCount) > 1;

  return (
    <ChatSidePanelCard
      title="参加者"
      count={membersVisible ? `${Number(participantCounts?.total || 0)}人` : null}
    >
      <div className="side-meta">
        担当者 {Number(participantCounts?.admins || 0)}人
        {hasConciergeParticipants && <> / 企画者 {Number(participantCounts?.concierge || 0)}人</>}
        {membersVisible ? (
          <> / 出演者 {Number(participantCounts?.artists || 0)}人 / 合計 {Number(participantCounts?.total || 0)}人</>
        ) : (
          <> / 出演者 非公開</>
        )}
      </div>
      {showScope ? (
        <div className="side-member-events side-member-events--scope">
          グループ統合チャット集計: {effectiveGroupEventCount}イベント
        </div>
      ) : null}

      <ChatSidePanelSection
        title="担当者"
        isEmpty={safeAdminParticipants.length === 0}
        emptyText="担当者情報がありません"
      >
        <ChatSidePanelPeopleList
          people={safeAdminParticipants}
          onSelect={onSelectAdmin}
          resolveSelectPayload={(entry) => entry?.payload}
          resolveRowProps={(person) => ({
            adminUserId: person?.adminUserId ?? person?.payload?.adminUserId ?? person?.payload?.admin_user_id,
            avatarStyle: person?.avatarUrl
              ? {
                  backgroundImage: `url(${person.avatarUrl})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }
              : {
                  backgroundColor: 'var(--admin-avatar-fallback-bg)',
                  color: 'var(--admin-avatar-fallback-text)',
                },
            avatarFallback: !person?.avatarUrl ? person?.initial || '' : '',
            showMeta: true,
          })}
        />
      </ChatSidePanelSection>

      {hasConciergeParticipants ? (
        <ChatSidePanelSection
          title="企画者"
          isEmpty={safeConciergeParticipants.length === 0}
          emptyText="企画者情報がありません"
        >
          <ChatSidePanelPeopleList
            people={safeConciergeParticipants}
            onSelect={onSelectConcierge}
            resolveSelectPayload={(entry) => entry?.payload}
            resolveRowProps={(person) => ({
              avatarStyle: person?.avatarUrl
                ? {
                    backgroundImage: `url(${person.avatarUrl})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }
                : getArtistFallbackAvatarStyle(person?.gender),
              avatarFallback: !person?.avatarUrl ? person?.initial || '' : '',
            })}
          />
        </ChatSidePanelSection>
      ) : null}

      <ChatSidePanelSection
        title="出演者"
        isEmpty={membersVisible ? safeArtistGroups.length === 0 : true}
        emptyText={membersVisible ? '出演者情報がありません' : hiddenArtistsText}
      >
        {safeTabs.length > 0 ? (
          <div className="side-event-part-tabs" role="tablist" aria-label="出演者パート表示の公演切り替え">
            {safeTabs.map((tab) => {
              const active = String(participantPartScopeEventUuid) === String(tab?.eventUuid);
              return (
                <button
                  key={`part-scope-${tab?.eventUuid}`}
                  type="button"
                  className={`side-event-part-tab${active ? ' is-active' : ''}`}
                  onClick={() => onChangeParticipantPartScope?.(tab?.eventUuid)}
                  role="tab"
                  aria-selected={active}
                >
                  {tab?.label || '公演'}
                </button>
              );
            })}
          </div>
        ) : null}

        {membersVisible && safeArtistGroups.length > 0 ? (
          <ChatSidePanelGroupedPeopleList
            groups={safeArtistGroups}
            resolveGroupKey={(group) => `artist-part-${group?.label}-${group?.order ?? 0}`}
            onSelect={onSelectArtist}
            resolveSelectPayload={(entry) => entry?.payload}
            resolveRowProps={(person, _personIndex, group) => ({
              avatarStyle: person?.avatarUrl
                ? {
                    backgroundImage: `url(${person.avatarUrl})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }
                : getArtistFallbackAvatarStyle(person?.gender),
              avatarFallback: !person?.avatarUrl ? person?.initial || '' : '',
              showBadges: false,
              showRolesAsText: true,
              sectionLabel: group?.label || '',
            })}
          />
        ) : null}
      </ChatSidePanelSection>
    </ChatSidePanelCard>
  );
}
